(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  class FlattenHero extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.progress = 0;
      this.photo = this.querySelector('.fte-hero__image');
      this.expandedBackground = this.querySelector('[data-expanded-background]');
      this.stage = this.querySelector('.fte-hero__visual') || this.querySelector('.fte-hero__sticky');
      this.panel = this.querySelector('.fte-hero__sticky');
      this.card = this.querySelector('.fte-hero__card');
      this.originalPhoto = this.dataset.originalSrc || this.photo?.getAttribute('src');
      this.originalSrcset = this.photo?.getAttribute('srcset');
      this.originalSources = [...this.querySelectorAll('.fte-hero__scene picture source')].map(source => [source, source.getAttribute('srcset')]);
      this.measure = () => {
        if (!this.photo || !this.stage) return;
        const mobile = innerWidth < 750;
        if (this.viewportWidth !== innerWidth) {
          this.playSkip = 0;
          // Read the CSS large viewport rather than iOS Safari's changing visual viewport.
          // A fixed scroll track and the pinned panel must measure identically.
          if (mobile) {
            const probe = document.createElement('div');
            probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:100lvh;width:0';
            this.appendChild(probe);
            this.viewportHeight = (Math.round(probe.getBoundingClientRect().height) || innerHeight) + 24;
            probe.remove();
          } else {
            this.viewportHeight = innerHeight;
          }
          this.viewportWidth = innerWidth;
        }
        if (!mobile) this.viewportHeight = innerHeight;
        const viewportHeight = this.viewportHeight || innerHeight;
        const pinEnabled = this.dataset.enabled === 'true' && !reduced.matches && !!this.panel;
        this.dataset.scrollPin = String(pinEnabled);
        const heroRect = this.getBoundingClientRect();
        if (pinEnabled) {
          const cardHeight=mobile && this.dataset.mobileCardOverlay !== 'true'?(this.card?.getBoundingClientRect().height||150):0;
          const photoHeight=mobile?Math.max(1,viewportHeight-cardHeight):viewportHeight;
          this.style.setProperty('--fte-mobile-photo-height', `${photoHeight}px`);
          this.pinHeight=viewportHeight;
          this.style.setProperty('--fte-pin-height', `${this.pinHeight}px`);
          this.style.setProperty('--fte-pin-left', `${heroRect.left}px`);
          this.style.setProperty('--fte-pin-width', `${heroRect.width}px`);
        } else {
          delete this.dataset.pinPhase;
        }
        const reference = this.dataset.heroFraming === 'tall_expanded' && !mobile;
        if (reference) {
          const headerHeight = Math.min(innerHeight * .25, Math.max(0, this.getBoundingClientRect().top + scrollY));
          this.style.setProperty('--fte-reference-available-height', `${Math.max(1, innerHeight - headerHeight)}px`);
        }
        const expanded = this.dataset.cleanScene === 'true' || ((mobile || this.dataset.expanded === 'true') && this.expandedBackground?.complete && this.expandedBackground.naturalWidth > 0);
        this.dataset.expandedReady = String(!!expanded);
        const targetSrc = this.originalPhoto;
        if (targetSrc && this.photo.getAttribute('src') !== targetSrc) {
          this.photo.setAttribute('src', targetSrc);
          if (expanded) this.photo.removeAttribute('srcset');
          else if (this.originalSrcset) this.photo.setAttribute('srcset', this.originalSrcset);
          for (const [source, srcset] of this.originalSources) {
            if (expanded) source.removeAttribute('srcset');
            else if (srcset) source.setAttribute('srcset', srcset);
          }
        }
        if (this.card) this.style.setProperty('--fte-mobile-card-height', `${this.card.getBoundingClientRect().height}px`);
        const { width, height } = this.stage.getBoundingClientRect();
        if (!width || !height) return;
        const referenceUnit = Math.min(width / 1047, height / 830);
        this.style.setProperty('--fte-reference-unit', `${referenceUnit}px`);
        this.style.setProperty('--fte-reference-height', `${referenceUnit * 830}px`);
        const iw = this.photo.naturalWidth || 1313;
        const ih = this.photo.naturalHeight || 1198;
        const bounds = this.dataset.cleanScene === 'true' ? {left:iw*-0.35,top:ih*-0.55,right:iw*1.35,bottom:ih*1.55} : expanded ? {left:iw*-0.19717361705640585,top:ih*-0.4831679762022581,right:iw*1.1891848783674204,bottom:ih*1.5961159480950662} : {left:0,top:0,right:iw,bottom:ih};
        const rear = [iw * Number(this.dataset.rearX ?? 360) / 1000, ih * Number(this.dataset.rearY ?? 889) / 1000];
        const front = [iw * Number(this.dataset.frontX ?? 637) / 1000, ih * Number(this.dataset.frontY ?? 824) / 1000];
        const pivot = [(rear[0] + front[0]) / 2, (rear[1] + front[1]) / 2];
        const anchor = reference ? [width * .5, Math.max(744.3358764648438 * referenceUnit, height * .85)] : [width * .5, height * .88];
        const end = this.dataset.sceneEnabled === 'false' ? 0 : Number(this.dataset.sceneEnd ?? 7);
        // Overscan covers every viewport corner at every angle, around the tires.
        // Its scale stays constant through the scroll: no camera zoom or floating frame.
        const margin = 2;
        let cover = 0;
        for (let step = 0; step <= 80; step++) {
          const theta = end * step / 80 * Math.PI / 180, c = Math.cos(theta), sn = Math.sin(theta);
          for (const [x,y] of [[0,0],[width,0],[width,height],[0,height]]) {
            const dx = x - anchor[0], dy = y - anchor[1];
            const sx = dx * c + dy * sn, sy = -dx * sn + dy * c;
            cover = Math.max(cover, sx / (sx < 0 ? bounds.left + margin - pivot[0] : bounds.right - margin - pivot[0]), sy / (sy < 0 ? bounds.top + margin - pivot[1] : bounds.bottom - margin - pivot[1]));
          }
        }
        const wheelbase = Math.hypot(front[0]-rear[0], front[1]-rear[1]);
        const bikeWidth = wheelbase * 1.44;
        const zoom = Number((mobile ? this.dataset.mobileZoom : this.dataset.zoom) ?? 100) / 100;
        const view = this.dataset.perspective || "original";
        const framing = view === "scenic" ? (mobile ? .72 : expanded ? .52 : .42) : view === "wide" ? (mobile ? .72 : expanded ? .58 : .50) : (mobile ? .82 : .62);
        const desired = reference ? 4096 * .245 * referenceUnit / iw * zoom : width * framing / bikeWidth * zoom;
        const scale = Math.max(cover * 1.015, desired);
        this.geometry = { width, height, iw, ih, rear, front, pivot, anchor, scale, cover, bounds, expanded:!!expanded };
        this.style.setProperty('--fte-scene-width', `${iw * scale}px`);
        this.style.setProperty('--fte-scene-height', `${ih * scale}px`);
        this.style.setProperty('--fte-scene-left', `${anchor[0] - pivot[0] * scale}px`);
        this.style.setProperty('--fte-scene-top', `${anchor[1] - pivot[1] * scale}px`);
        this.style.setProperty('--fte-origin-x', `${pivot[0] * scale}px`);
        this.style.setProperty('--fte-origin-y', `${pivot[1] * scale}px`);
        const svg = this.querySelector('.fte-hero__shadow-projection');
        svg?.setAttribute('viewBox', `0 0 ${iw} ${ih}`);
        this.renderShadow?.();
      };
      this.photoShadow = window.FteLayers ? new window.FteLayers.PhotoShadow(this) : null;
      this.play = this.querySelector('[data-play-tilt]');
      this.paused = false;
      this.toggle = this.querySelector('[data-motion-toggle]');
      this.update = timestamp => {
        this.frame = 0;
        const enabled = this.dataset.enabled === 'true' && !reduced.matches;
        this.dataset.static = String(!enabled);
        if (!enabled) {this.replayStarted = null;this.replayDone=false;}
        if (this.toggle) this.toggle.hidden = !enabled;
        if (this.play) this.play.hidden = !enabled;
        if (this.paused && enabled) return;
        const compact = innerWidth < 750 && this.stage;
        const viewportHeight = this.pinHeight || innerHeight;
        const distance = Math.max(1, Math.min(viewportHeight * Number(this.dataset.distance ?? 45) / 100, viewportHeight * .8));
        const hold = Math.min(viewportHeight * Number(this.dataset.hold ?? 0) / 100, viewportHeight * .04);
        const endHold = innerWidth < 750 ? 0 : viewportHeight * .24;
        const fullTravel = enabled ? distance + hold + endHold : 0;
        let travel = Math.max(0, fullTravel - (this.playSkip || 0));
        this.motionRange = {distance, hold, compact:!!compact};
        this.style.setProperty('--fte-scroll-space', `${travel}px`);
        const scroll = -this.getBoundingClientRect().top;
        if (this.dataset.scrollPin === 'true') this.dataset.pinPhase = scroll < 0 ? 'before' : scroll < travel ? 'pinned' : 'after';
        let target = enabled ? Math.max(0, Math.min(1, (scroll + (this.playSkip || 0) - hold) / distance)) : 1;
        if (target >= .999) target = 1;
        else if (target <= .001) target = 0;
        if (this.replayDone && enabled) target = 1;
        if (this.replayStarted && enabled) {
          target = Math.min(1, (performance.now() - this.replayStarted) / (Number(this.dataset.playDuration || 2.5)*1000));
          if (target >= 1) {
            const endY = scrollY + this.getBoundingClientRect().top + travel;
            if (compact && this.dataset.scrollPin === 'true') {
              this.playSkip = Math.max(0, fullTravel - Math.max(0, scroll));
              travel = Math.max(0, fullTravel - this.playSkip);
              this.style.setProperty('--fte-scroll-space', `${travel}px`);
              this.dataset.pinPhase = scroll < 0 ? 'before' : 'after';
            } else if (this.dataset.scrollPin === 'true' && scrollY < endY) {
              const root = document.documentElement, behavior = root.style.scrollBehavior;
              root.style.scrollBehavior = 'auto';
              window.scrollTo({top:endY,left:window.scrollX,behavior:'instant'});
              root.style.scrollBehavior = behavior;
              this.dataset.pinPhase = 'after';
            }
            this.replayStarted = null;this.replayDone=true;this.play?.setAttribute('aria-pressed','false');this.play?.setAttribute('aria-label','Replay tilt animation');}
        }
        const easing = enabled && target > 0 && target < 1 && Number.isFinite(timestamp) ? Number(this.dataset.smoothing ?? 0) : 0;
        const delta = Math.min(64, Math.max(1, (timestamp || 0) - (this.lastTime || (timestamp || 0) - 16)));
        this.lastTime = timestamp;
        this.progress += (target - this.progress) * (easing ? 1 - Math.exp(-delta / (easing / 3)) : 1);
        if (Math.abs(this.progress - target) < .001) this.progress = target;
        if (this.replayStarted || Math.abs(this.progress - target) > .001) this.frame = requestAnimationFrame(this.update);
        this.style.setProperty('--fte-angle', `${Number(this.dataset.angle ?? 10) * (1 - (this.dataset.headlineEnabled === 'false' ? 0 : this.progress))}deg`);
        this.style.setProperty('--fte-scene-angle', `${Number(this.dataset.sceneEnd ?? 7) * (this.dataset.sceneEnabled === 'false' ? 0 : this.progress)}deg`);
        this.style.setProperty('--fte-progress', this.progress);
        this.dataset.complete=String(this.progress>=.999);
        this.style.setProperty('--fte-graphics-angle', `${Number(this.dataset.graphicsRotation ?? 7) * (this.dataset.graphicsEnabled === 'false' ? 0 : this.progress)}deg`);
        this.style.setProperty('--fte-graphics-opacity', this.dataset.showGraphics === 'false' ? 0 : Number(this.dataset.graphicsOpacity ?? 35) / 100);
        this.renderShadow();
      };
      this.renderShadow = () => {
        const sceneAngle = Number(this.dataset.sceneEnd ?? 7) * (this.dataset.sceneEnabled === 'false' ? 0 : this.progress);
        const lightStart = Number(this.dataset.lightStart ?? 235);
        const lightAngle = lightStart + (this.dataset.lightEnabled === 'true' ? (Number(this.dataset.lightEnd ?? 285) - lightStart) * this.progress : 0);
        // The same screen-space light drives the overlay AND the cast shadow.
        this.style.setProperty('--fte-light-angle', `${lightAngle + 90 - sceneAngle}deg`);
        this.style.setProperty('--fte-light-opacity', this.dataset.lightEnabled === 'true' ? Number(this.dataset.lightStrength ?? 10) / 100 : 0);
        this.photoShadow?.draw();

      };
      this.schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(this.update); };
      window.addEventListener('scroll', () => {this.replayDone=false;this.schedule();}, { passive: true, signal });
      window.addEventListener('resize', () => { this.measure(); this.schedule(); }, { passive: true, signal });
      this.photo?.addEventListener('load', () => { this.measure(); this.schedule(); }, { signal });
      this.expandedBackground?.addEventListener('load', () => { this.measure(); this.schedule(); }, { signal });
      this.expandedBackground?.addEventListener('error', () => { this.dataset.expandedReady='false';this.measure();this.schedule(); }, { signal });
      this.resize = new ResizeObserver(() => { this.measure(); this.schedule(); });
      if (this.stage) this.resize.observe(this.stage);
      if (this.card) this.resize.observe(this.card);
      reduced.addEventListener('change', () => { this.measure(); this.schedule(); }, { signal });
      this.toggle?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.toggle.setAttribute('aria-pressed', String(this.paused));
        this.toggle.querySelector('[data-pause]').hidden = this.paused;
        this.toggle.querySelector('[data-resume]').hidden = !this.paused;
        this.schedule();
      }, { signal });
      this.querySelector('[data-replay]')?.addEventListener('click', () => {
        if (reduced.matches) return;
        this.scrollIntoView({behavior:'smooth', block:'start'});
        this.paused = false; this.progress = 0; this.playSkip=0; this.replayDone=false; this.replayStarted = performance.now();
        if(this.toggle){this.toggle.setAttribute('aria-pressed','false');this.toggle.querySelector('[data-pause]').hidden=false;this.toggle.querySelector('[data-resume]').hidden=true;}
        this.schedule();
      }, {signal});
      this.play?.addEventListener('click', () => {
        if(reduced.matches)return;
        if(this.replayStarted){this.replayStarted=null;this.paused=true;this.play.setAttribute('aria-pressed','false');this.play.setAttribute('aria-label','Play tilt animation');}
        else {this.paused=false;this.progress=0;this.playSkip=0;this.replayDone=false;this.replayStarted=performance.now();this.play.setAttribute('aria-pressed','true');this.play.setAttribute('aria-label','Pause tilt animation');}
        this.schedule();
      },{signal});
      this.measure();
      this.update();
    }
    disconnectedCallback() { this.controller?.abort(); this.resize?.disconnect(); cancelAnimationFrame(this.frame); }
  }
  class DealSlider extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('.fte-slider__track, .fte-gallery__track');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.bikeCta = this.closest('.fte-sale-panel')?.querySelector('[data-bike-cta]');
      const isBikeSlider = this.matches('fte-slider');
      this.bikesReady = !isBikeSlider;
      if (isBikeSlider) {
        this.track.dataset.bikesReady = 'false';
        this.track.setAttribute('aria-busy', 'true');
        this.track.style.overflowX = 'hidden';
        this.track.style.touchAction = 'pan-y';
      }

      this.platform = window.FteLayers && this.matches('fte-slider') ? new window.FteLayers.Platform(this) : null;
      this.update = () => {
        const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
        this.track.dataset.swipeable=String(overflow && this.bikesReady);
        const controls = this.querySelector('.fte-slider__controls');
        if (controls) controls.hidden = !overflow;
        if (this.prev) this.prev.disabled = !this.bikesReady || this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = !this.bikesReady || this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
        const index=Math.min(this.track.children.length-1,Math.round(this.track.scrollLeft/Math.max(1,this.track.clientWidth)));
        if(this.lastSmokeScroll!==undefined&&Math.abs(this.track.scrollLeft-this.lastSmokeScroll)>2){
          this.transitionSmoke();clearTimeout(this.smokeGestureTimer);this.smokeGestureTimer=setTimeout(()=>{this.smokeGestureActive=false;},180);
        }
        this.lastSmokeScroll=this.track.scrollLeft;
        if(this.bikeCta){
          const bike=this.track.children[index],url=bike?.dataset.ctaUrl||'';
          this.bikeCta.hidden=!url;
          if(url)this.bikeCta.setAttribute('href',url);
          else this.bikeCta.removeAttribute('href');
          this.bikeCta.textContent=bike?.dataset.ctaLabel||'VIEW BIKE';
        }
        this.currentIndex=index;
        this.track.setAttribute('aria-label',this.matches('fte-slider') ? `Featured bike: ${index+1} of ${this.track.children.length}` : 'Customer gallery');
      };
      const move = direction => {
        if (!this.bikesReady) return;
        this.cancelSettle?.();
        const step = this.matches('fte-gallery') ? this.track.children[0]?.getBoundingClientRect().width + 28 : this.track.clientWidth;
        const target=Math.max(0,Math.min(this.track.scrollWidth-this.track.clientWidth,this.track.scrollLeft+direction*step));
        if(Math.abs(target-this.track.scrollLeft)>2)this.transitionSmoke(true);
        this.track.scrollBy({ left: direction * step, behavior: reduced.matches ? 'instant' : 'smooth' });
      };
      this.transitionSmoke = (force=false) => {
        if (!this.bikesReady || reduced.matches || !this.matches('fte-slider') || (this.smokeGestureActive&&!force)) return;
        this.smokeGestureActive=true;
        this.platform?.burst();
        // Movement stays native and continuous; the puff never restarts a bike opacity animation.
      };
      this.prev?.addEventListener('click', () => move(-1), { signal });
      this.next?.addEventListener('click', () => move(1), { signal });
      this.track.addEventListener('scroll', this.update, { passive: true, signal });
      this.track.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); move(e.key === 'ArrowRight' ? 1 : -1); }
      }, { signal });
      this.track.tabIndex = 0;
      // Touch uses the browser's compositor scrolling. Mouse release settles without
      // re-enabling scroll snap mid-gesture (which caused the previous visible jump).
      let drag=null,suppressClick=false;

      this.cancelSettle=()=>{cancelAnimationFrame(this.settleFrame);this.settleFrame=0;this.track.classList.remove('is-settling');};
      this.settleTo=left=>{
        const from=this.track.scrollLeft,delta=left-from,start=performance.now();
        this.track.classList.add('is-settling');this.track.classList.remove('is-dragging');
        const duration=reduced.matches?0:Math.min(480,240+Math.abs(delta)*.25);
        const tick=now=>{const t=duration?Math.min(1,(now-start)/duration):1;
          this.track.scrollLeft=from+delta*(1-Math.pow(1-t,3));
          if(t<1)this.settleFrame=requestAnimationFrame(tick);
          else{this.settleFrame=0;this.track.classList.remove('is-settling');this.update();}
        };this.settleFrame=requestAnimationFrame(tick);
      };
      let touchDrag=null;
      this.addEventListener('touchstart',e=>{
        if(e.touches.length!==1||this.track.dataset.swipeable!=='true'||e.target.closest('a,button,input,select'))return;
        this.cancelSettle();
        const t=e.touches[0];
        touchDrag={id:t.identifier,x:t.clientX,y:t.clientY,lastX:t.clientX,time:performance.now(),velocity:0,left:this.track.scrollLeft,axis:null};
      },{passive:true,signal});
      this.addEventListener('touchmove',e=>{
        if(!touchDrag)return;
        if(e.touches.length!==1){touchDrag=null;this.track.classList.remove('is-dragging');return;}
        const t=e.touches[0];if(t.identifier!==touchDrag.id)return;
        const dx=t.clientX-touchDrag.x,dy=t.clientY-touchDrag.y;
        if(!touchDrag.axis){
          if(Math.max(Math.abs(dx),Math.abs(dy))<6)return;
          touchDrag.axis=Math.abs(dx)>Math.abs(dy)?'x':'y';
        }
        if(touchDrag.axis!=='x')return;
        if(!e.cancelable){touchDrag=null;return;}
        e.preventDefault();
        const now=performance.now();
        touchDrag.velocity=(t.clientX-touchDrag.lastX)/Math.max(1,now-touchDrag.time);
        touchDrag.lastX=t.clientX;touchDrag.time=now;
        this.track.classList.add('is-dragging');
        this.track.scrollLeft=touchDrag.left-dx;
      },{passive:false,signal});
      const endTouch=cancelled=>{
        const ended=touchDrag;touchDrag=null;
        if(!ended||ended.axis!=='x'){this.track.classList.remove('is-dragging');return;}
        suppressClick=true;setTimeout(()=>{suppressClick=false;},0);
        const step=this.matches('fte-gallery')?this.track.children[0].getBoundingClientRect().width+28:this.track.clientWidth;
        const delta=this.track.scrollLeft-ended.left,velocity=!cancelled&&performance.now()-ended.time<100?ended.velocity:0;
        const start=Math.round(ended.left/step);
        let target=Math.round((this.track.scrollLeft-velocity*180)/step);
        if(!cancelled&&Math.abs(delta)>step*.18)target=start+(delta>0?1:-1);
        target=Math.max(0,Math.min(this.track.children.length-1,Math.max(start-1,Math.min(start+1,target))));
        this.settleTo(Math.min(this.track.scrollWidth-this.track.clientWidth,target*step));
      };
      this.addEventListener('touchend',()=>endTouch(false),{passive:true,signal});
      this.addEventListener('touchcancel',()=>endTouch(true),{passive:true,signal});
      this.track.addEventListener('pointerdown',e=>{
        if(e.pointerType!=='mouse'||e.button!==0||this.track.dataset.swipeable!=='true'||e.target.closest('a,button,input,select'))return;
        this.cancelSettle();
        drag={id:e.pointerId,x:e.clientX,lastX:e.clientX,time:performance.now(),velocity:0,left:this.track.scrollLeft,moved:false};
      },{signal});
      this.track.addEventListener('pointermove',e=>{
        if(!drag||e.pointerId!==drag.id)return;
        const dx=e.clientX-drag.x,now=performance.now(),dt=Math.max(1,now-drag.time);
        drag.velocity=(e.clientX-drag.lastX)/dt;drag.lastX=e.clientX;drag.time=now;
        if(!drag.moved&&Math.abs(dx)<5)return;
        if(!drag.moved){drag.moved=true;this.track.classList.add('is-dragging');this.track.setPointerCapture?.(e.pointerId);}
        e.preventDefault();this.track.scrollLeft=drag.left-dx;
      },{signal});
      const release=()=>{
        if(!drag)return;const ended=drag;drag=null;
        if(this.track.hasPointerCapture?.(ended.id))this.track.releasePointerCapture(ended.id);
        if(ended.moved){
          suppressClick=true;setTimeout(()=>{suppressClick=false;},0);
          const first=this.track.children[0],step=this.matches('fte-gallery')?first.getBoundingClientRect().width+28:this.track.clientWidth;
          const velocity=performance.now()-ended.time<100?ended.velocity:0;
          const start=Math.round(ended.left/step),projected=(this.track.scrollLeft-velocity*180)/step;
          let target=Math.round(projected);
          if(Math.abs(this.track.scrollLeft-ended.left)>step*.18)target=this.track.scrollLeft<ended.left?Math.floor(projected):Math.ceil(projected);
          target=Math.max(0,Math.min(this.track.children.length-1,Math.max(start-1,Math.min(start+1,target))));
          this.settleTo(Math.min(this.track.scrollWidth-this.track.clientWidth,target*step));
        }else this.track.classList.remove('is-dragging');
      };
      this.track.addEventListener('pointerup',release,{signal});this.track.addEventListener('pointercancel',release,{signal});
      this.track.addEventListener('lostpointercapture',release,{signal});
      this.track.addEventListener('pointerleave',()=>{if(drag&&!drag.moved)drag=null;},{signal});
      this.track.addEventListener('click',e=>{if(suppressClick){e.preventDefault();e.stopPropagation();suppressClick=false;}},{capture:true,signal});
      this.track.addEventListener('dragstart',e=>e.preventDefault(),{signal});
      this.track.addEventListener('wheel',this.cancelSettle,{passive:true,signal});
      this.resize = new ResizeObserver(this.update);
      this.resize.observe(this.track);
      this.update();

      if (isBikeSlider) {
        const images = [...this.track.querySelectorAll('.fte-deal__art img')];
        const decodeBike = async image => {
          image.loading = 'eager';
          image.decoding = 'async';
          if (typeof image.decode === 'function') {
            try { await image.decode(); }
            catch { return image.complete && image.naturalWidth > 0; }
            return image.naturalWidth > 0;
          }
          if (image.complete) return image.naturalWidth > 0;
          return new Promise(resolve => {
            image.addEventListener('load', () => resolve(image.naturalWidth > 0), { once: true, signal });
            image.addEventListener('error', () => resolve(false), { once: true, signal });
            signal.addEventListener('abort', () => resolve(false), { once: true });
          });
        };
        Promise.all(images.map(decodeBike)).then(results => {
          if (signal.aborted) return;
          this.bikesReady = images.length > 0 && results.every(Boolean);
          this.track.dataset.bikesReady = this.bikesReady ? 'true' : 'error';
          this.track.removeAttribute('aria-busy');
          if (this.bikesReady) {
            this.track.style.removeProperty('overflow-x');
            this.track.style.removeProperty('touch-action');
            this.update();
          } else {
            this.track.setAttribute('aria-label', 'Featured bike images did not load. Refresh to retry.');
          }
        });
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.resize?.disconnect(); clearTimeout(this.smokeTimer);clearTimeout(this.smokeGestureTimer); cancelAnimationFrame(this.settleFrame); this.platform?.destroy(); }
  }
  class CampaignVideo extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.querySelector('button')?.addEventListener('click', () => {
        const template = this.querySelector('template');
        if (!template) return;
        this.replaceChildren(template.content.cloneNode(true));
        const player = this.querySelector('video,iframe');
        if (player instanceof HTMLVideoElement) {
          // Use one consistent set of controls. Browser-native timelines on
          // some Android devices don't seek reliably with Shopify-hosted video.
          player.controls = false;
          player.preload = 'auto';
          player.setAttribute('playsinline', '');
          const controls = document.createElement('div');
          controls.className = 'fte-video__seek-controls';
          controls.setAttribute('role', 'group');
          controls.setAttribute('aria-label', 'Video playback controls');
          const makeButton = (label, glyph) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'fte-video__transport-button';
            button.setAttribute('aria-label', label);
            button.textContent = glyph;
            return button;
          };
          const playPause = makeButton('Pause video', '');
          playPause.classList.add('fte-video__play-pause');
          const mute = makeButton('Mute video', '');
          mute.classList.add('fte-video__mute');
          const volumeOnIcon = '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5Z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>';
          const volumeOffIcon = '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5Z"/><path d="m15 9 6 6m0-6-6 6"/></svg>';
          const fullscreen = makeButton('Enter fullscreen', '⛶');
          const seek = document.createElement('div');
          seek.className = 'fte-video__seek';
          seek.setAttribute('role', 'slider');
          seek.setAttribute('tabindex', '0');
          seek.setAttribute('aria-label', 'Seek video playback');
          seek.setAttribute('aria-valuemin', '0');
          seek.setAttribute('aria-valuemax', '0');
          seek.setAttribute('aria-valuenow', '0');
          const seekFill = document.createElement('span');
          seekFill.className = 'fte-video__seek-fill';
          const seekThumb = document.createElement('span');
          seekThumb.className = 'fte-video__seek-thumb';
          seek.append(seekFill, seekThumb);
          const time = document.createElement('span');
          time.className = 'fte-video__time';
          time.textContent = '0:00 / 0:00';
          const fmt = seconds => {
            const t = Math.max(0, Math.floor(Number(seconds) || 0));
            return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0');
          };
          const duration = () => Number.isFinite(player.duration) ? player.duration : 0;
          let scrubbing = false;
          let previewTime = 0;
          let activePointer = null;
          let pendingSeek = null;
          const showPosition = seconds => {
            const d = duration();
            const position = d > 0 ? Math.max(0, Math.min(seconds, d)) : 0;
            seek.style.setProperty('--seek-progress', (d > 0 ? position / d * 100 : 0) + '%');
            seek.setAttribute('aria-valuemax', String(Math.floor(d)));
            seek.setAttribute('aria-valuenow', String(Math.floor(position)));
            seek.setAttribute('aria-valuetext', fmt(position) + ' of ' + fmt(d));
            time.textContent = fmt(position) + ' / ' + fmt(d);
            seek.setAttribute('aria-disabled', d <= 0 ? 'true' : 'false');
          };
          const syncTransport = () => {
            playPause.classList.toggle('is-paused', player.paused);
            playPause.setAttribute('aria-label', player.paused ? 'Play video' : 'Pause video');
            mute.innerHTML = player.muted ? volumeOffIcon : volumeOnIcon;
            mute.setAttribute('aria-label', player.muted ? 'Unmute video' : 'Mute video');
            if (!scrubbing) showPosition(pendingSeek === null ? player.currentTime : pendingSeek);
          };
          const positionFromPointer = event => {
            const rect = seek.getBoundingClientRect();
            if (!rect.width || !duration()) return null;
            // Android may emit a synthetic 0,0 pointer move after capture.
            // Ignore out-of-range coordinates rather than seeking to zero.
            if (!Number.isFinite(event.clientX) || event.clientX < rect.left - 12 || event.clientX > rect.right + 12) return null;
            return Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) * duration();
          };
          // Some mobile video pipelines report a duration but cannot seek
          // outside the initial downloaded portion. After a failed seek, load
          // a single progressive MP4 into a blob URL. This creates a locally
          // seekable file instead of replaying currentTime assignments.
          let seekRequestId = 0;
          let seekCheckTimer = null;
          let localVideoUrl = null;
          let localVideoPromise = null;
          let localVideoActive = false;
          let localVideoFailed = false;
          this.controller.signal.addEventListener('abort', () => {
            clearTimeout(seekCheckTimer);
            if (localVideoUrl) URL.revokeObjectURL(localVideoUrl);
          }, { once: true });
          const isRangeSeekable = target => {
            const ranges = player.seekable;
            for (let i = 0; i < ranges.length; i++) {
              if (target >= ranges.start(i) && target <= ranges.end(i)) return true;
            }
            return false;
          };
          const loadLocally = async (target, requestId) => {
            if (localVideoActive || localVideoFailed || !player.isConnected) return;
            if (!localVideoPromise) {
              localVideoPromise = (async () => {
                const url = player.dataset.seekFallbackUrl || player.currentSrc;
                if (!url) throw new Error('No seekable video source');
                const response = await fetch(url, { mode: 'cors' });
                if (!response.ok) throw new Error('Seekable video fetch failed');
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                localVideoUrl = objectUrl;
                return objectUrl;
              })();
            }
            try {
              const url = await localVideoPromise;
              if (!url || !player.isConnected || this.controller.signal.aborted) return;
              if (localVideoActive) return;
              localVideoActive = true;
              const wasPlaying = !player.paused;
              player.pause();
              player.src = url;
              player.load();
              await new Promise((resolve, reject) => {
                if (player.readyState >= 1) return resolve();
                const loaded = () => { cleanup(); resolve(); };
                const failed = () => { cleanup(); reject(new Error('Local video metadata failed')); };
                const cleanup = () => {
                  player.removeEventListener('loadedmetadata', loaded);
                  player.removeEventListener('error', failed);
                };
                player.addEventListener('loadedmetadata', loaded, { once: true });
                player.addEventListener('error', failed, { once: true });
              });
              if (!player.isConnected) return;
              const destination = pendingSeek === null ? target : pendingSeek;
              player.currentTime = Math.max(0, Math.min(destination, player.duration - 0.1));
              // Let the decoder finish the seek before resuming playback.
              // Calling play() immediately can force some Android players to 0.
              await new Promise(resolve => {
                let timer;
                const settled = () => {
                  clearTimeout(timer);
                  player.removeEventListener('seeked', settled);
                  resolve();
                };
                player.addEventListener('seeked', settled, { once: true });
                timer = setTimeout(settled, 1800);
              });
              pendingSeek = null;
              syncTransport();
              if (wasPlaying) player.play().catch(() => syncTransport());
            } catch (error) {
              pendingSeek = null;
              syncTransport();
              // Avoid additional network attempts if the fallback cannot load.
              localVideoFailed = true;
              localVideoPromise = null;
            }
          };
          const commitSeek = position => {
            const d = duration();
            if (!d || position === null || !Number.isFinite(position)) return;
            const target = Math.max(0, Math.min(position, d - 0.1));
            const requestId = ++seekRequestId;
            clearTimeout(seekCheckTimer);
            pendingSeek = target;
            showPosition(target);
            if (localVideoPromise && !localVideoActive) return;
            if (!localVideoActive && !localVideoFailed && !isRangeSeekable(target)) {
              loadLocally(target, requestId);
              return;
            }
            try { player.currentTime = target; }
            catch (_) {
              if (!localVideoActive) loadLocally(target, requestId);
              else { pendingSeek = null; syncTransport(); }
              return;
            }
            if (localVideoActive) {
              pendingSeek = null;
              return;
            }
            seekCheckTimer = setTimeout(() => {
              if (requestId !== seekRequestId || !player.isConnected) return;
              if (Math.abs(player.currentTime - target) > 1.5 && !localVideoFailed) {
                loadLocally(target, requestId);
              } else {
                pendingSeek = null;
                syncTransport();
              }
            }, 900);
          };
          player.addEventListener('seeked', () => {
            if (localVideoActive) {
              pendingSeek = null;
              if (!scrubbing) syncTransport();
            }
          }, { signal: this.controller.signal });
          player.addEventListener('error', () => {
            if (!localVideoActive && pendingSeek !== null) loadLocally(pendingSeek, seekRequestId);
          }, { signal: this.controller.signal });
          seek.addEventListener('pointerdown', event => {
            if (!duration() || (event.pointerType === 'mouse' && event.button !== 0)) return;
            const firstPosition = positionFromPointer(event);
            if (firstPosition === null) return;
            scrubbing = true;
            previewTime = firstPosition;
            showPosition(previewTime);
            activePointer = event.pointerId;
            try { seek.setPointerCapture(event.pointerId); } catch (_) {}
            event.preventDefault();
            event.stopPropagation();
          }, { signal: this.controller.signal });
          seek.addEventListener('pointermove', event => {
            if (!scrubbing || event.pointerId !== activePointer) return;
            const nextPosition = positionFromPointer(event);
            if (nextPosition !== null) {
              previewTime = nextPosition;
              showPosition(previewTime);
            }
            event.preventDefault();
          }, { signal: this.controller.signal });
          const finishSeek = event => {
            if (!scrubbing || event.pointerId !== activePointer) return;
            // Some Android browsers report clientX=0 on pointerup after capture.
            // Commit the last valid drag position, never the release event's X.
            const target = previewTime;
            scrubbing = false;
            activePointer = null;
            if (seek.hasPointerCapture(event.pointerId)) seek.releasePointerCapture(event.pointerId);
            commitSeek(target);
            event.preventDefault();
            event.stopPropagation();
          };
          seek.addEventListener('pointerup', finishSeek, { signal: this.controller.signal });
          seek.addEventListener('lostpointercapture', () => {
            if (!scrubbing) return;
            const target = previewTime;
            scrubbing = false;
            activePointer = null;
            commitSeek(target);
          }, { signal: this.controller.signal });
          seek.addEventListener('pointercancel', event => {
            if (event.pointerId !== activePointer) return;
            scrubbing = false;
            activePointer = null;
            syncTransport();
          }, { signal: this.controller.signal });
          seek.addEventListener('keydown', event => {
            const d = duration();
            if (!d) return;
            const jumps = {
              ArrowLeft: -5, ArrowRight: 5, ArrowDown: -5, ArrowUp: 5,
              PageDown: -10, PageUp: 10, Home: -Infinity, End: Infinity
            };
            if (!(event.key in jumps)) return;
            event.preventDefault();
            let target = player.currentTime + jumps[event.key];
            if (event.key === 'Home') target = 0;
            if (event.key === 'End') target = d;
            commitSeek(target);
          }, { signal: this.controller.signal });
          const togglePlayback = () => {
            if (player.paused) player.play().catch(() => { syncTransport(); });
            else player.pause();
          };
          playPause.addEventListener('click', togglePlayback, { signal: this.controller.signal });
          player.addEventListener('click', togglePlayback, { signal: this.controller.signal });
          player.style.cursor = 'pointer';
          player.setAttribute('aria-label', 'Video: click to play or pause');
          mute.addEventListener('click', () => { player.muted = !player.muted; syncTransport(); }, { signal: this.controller.signal });
          fullscreen.addEventListener('click', () => {
            if (document.fullscreenElement) document.exitFullscreen?.();
            else if (this.requestFullscreen) this.requestFullscreen().catch(() => {
              if (player.webkitEnterFullscreen) player.webkitEnterFullscreen();
            });
            else if (player.webkitEnterFullscreen) player.webkitEnterFullscreen();
          }, { signal: this.controller.signal });
          for (const name of ['loadedmetadata','durationchange','timeupdate','seeked','play','pause','volumechange','ended']) {
            player.addEventListener(name, syncTransport, { signal: this.controller.signal });
          }
          controls.append(playPause, seek, time, mute, fullscreen);
          this.append(controls);
          syncTransport();
          // Native CC menus are inconsistent on Android and iOS. Always provide
          // an explicit captions toggle when a VTT track is present.
          const trackElement = player.querySelector('track[kind="captions"], track[kind="subtitles"]');
          if (trackElement) {
            const ccButton = document.createElement('button');
            ccButton.type = 'button';
            ccButton.className = 'fte-video__cc';
            ccButton.textContent = 'CC';
            ccButton.setAttribute('aria-label', 'Turn closed captions on');
            ccButton.setAttribute('aria-pressed', 'false');
            ccButton.setAttribute('title', 'Closed captions');
            ccButton.classList.add('fte-video__transport-button');
            controls.insertBefore(ccButton, fullscreen);
            const tracks = player.textTracks;
            const captionTrack = tracks?.[0];
            if (captionTrack) captionTrack.mode = 'disabled';
            ccButton.addEventListener('click', () => {
              const currentTrack = player.textTracks?.[0];
              if (!currentTrack) return;
              const enabled = currentTrack.mode !== 'showing';
              currentTrack.mode = enabled ? 'showing' : 'disabled';
              ccButton.setAttribute('aria-pressed', String(enabled));
              ccButton.setAttribute('aria-label', enabled ? 'Turn closed captions off' : 'Turn closed captions on');
            }, { signal: this.controller.signal });
          }
          // This player appears only after an explicit Play click: start with sound.
          // Do not rely on muted autoplay, which can suppress audio on mobile.
          player.removeAttribute('muted');
          player.defaultMuted = false;
          player.muted = false;
          player.volume = 1;
          const playback = player.play();
          if (playback && typeof playback.catch === 'function') {
            playback.catch(() => {
              // Keep the single custom control bar visible for a manual retry.
              syncTransport();
            });
          }
        }
        player?.setAttribute('tabindex', '0');
        player?.focus();
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class TiltStrip extends HTMLElement {
    connectedCallback() {
      this.controller=new AbortController();const {signal}=this.controller;
      this.paint=p=>{const from=Number(this.dataset.from??-4),to=Number(this.dataset.to??0),angle=from+(to-from)*p;this.style.setProperty('--fte-strip-angle',`${angle}deg`);this.dataset.progress=p.toFixed(3);};
      // The strip remains in its original document flow: no sticky pin or spacer.
      this.measure=()=>{
        this.dataset.pinned='false';
        const panel=this.querySelector('.fte-benefits-panel');if(!panel)return;
        const width=this.clientWidth,height=panel.offsetHeight;
        const angle=Math.min(12,Math.max(Math.abs(Number(this.dataset.from??-6)),Math.abs(Number(this.dataset.to??0))))*Math.PI/180;
        // Cover the viewport even when a tall mobile panel rotates. Never scale the text.
        const bleed=Math.ceil((height*Math.sin(angle)+width*(1-Math.cos(angle)))/(2*Math.cos(angle)))+2;
        const overlap=Math.ceil((width/2+bleed)*Math.sin(angle)+height/2*(1-Math.cos(angle)))+2;
        this.style.setProperty('--fte-strip-bleed',`${bleed}px`);
        this.style.setProperty('--fte-strip-width',`${width+bleed*2}px`);
        this.style.setProperty('--fte-strip-overlap',`${overlap}px`);
        const following=this.closest('.fte-section')?.nextElementSibling?.querySelector('.fte-sale-panel');
        const stageOffset=innerWidth<750?0:Number(following?.dataset.stageY??-4)*Math.min(width,1600)/100;
        this.style.setProperty('--fte-stage-join-y',`${stageOffset}px`);
        const previous=this.closest('.fte-section')?.previousElementSibling,scene=previous?.querySelector('.fte-target-scene');
        const lead=innerWidth<750&&scene?Math.max(0,previous.getBoundingClientRect().bottom-scene.getBoundingClientRect().bottom):0;
        this.style.setProperty('--fte-strip-lead',`${Math.ceil(lead)}px`);
        this.style.setProperty('--fte-strip-min-height',`${Math.max(304,(width+bleed*2)*.2291)}px`);
      };
      this.update=()=>{
        this.frame=0;
        if(this.dataset.enabled!=='true'||reduced.matches){this.started=null;this.paint(0);return;}
        const r=this.getBoundingClientRect(),trigger=innerHeight*Number(this.dataset.trigger??85)/100;
        if(this.dataset.mode!=='entry'){
          const hold=this.dataset.mode==='scroll'?innerHeight*Number(this.dataset.hold??20)/100:0;
          const start=trigger-hold;
          // Finish while the strip is still visible, even on a short mobile section.
          const visibleTravel=Math.max(innerHeight*.2,start+r.height*.25);
          const distance=Math.min(innerHeight*Number(this.dataset.distance??(this.dataset.mode==='scroll'?110:65))/100,visibleTravel);
          const t=Math.max(0,Math.min(1,(start-r.top)/distance));
          this.paint(t*t*(3-2*t));return;
        }
        if(this.started==null&&r.top<=trigger&&r.bottom>0)this.started=performance.now()+Number(this.dataset.delay??500);
        if(this.started==null){this.paint(0);return;}
        const t=Math.max(0,Math.min(1,(performance.now()-this.started)/Number(this.dataset.duration??2400)));
        this.paint(t*t*(3-2*t));
        if(t<1&&!document.hidden)this.frame=requestAnimationFrame(this.update);
      };
      this.schedule=()=>{if(!this.frame)this.frame=requestAnimationFrame(this.update);};
      this.replay=()=>{this.started=null;cancelAnimationFrame(this.frame);this.frame=0;this.measure();this.update();};
      addEventListener('scroll',this.schedule,{passive:true,signal});addEventListener('resize',this.replay,{passive:true,signal});
      document.addEventListener('visibilitychange',this.schedule,{signal});reduced.addEventListener('change',this.replay,{signal});
      this.options=new MutationObserver(this.replay);this.options.observe(this,{attributes:true,attributeFilter:['data-enabled','data-mode','data-from','data-to','data-trigger','data-delay','data-duration','data-distance','data-hold']});
      this.resize=new ResizeObserver(this.replay);const hold=this.querySelector('.fte-tilt__hold');if(hold)this.resize.observe(hold);this.measure();this.update();
    }
    disconnectedCallback(){this.controller?.abort();this.options?.disconnect();this.resize?.disconnect();cancelAnimationFrame(this.frame);}
  }
  if (!customElements.get('fte-tilt')) customElements.define('fte-tilt',TiltStrip);
  class ProtractorSweep extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller=new AbortController();
      const {signal}=this.controller;
      this.needle=this.querySelector('[data-protractor-needle]');
      this.update=()=>{
        this.frame=0;
        if(!this.needle)return;
        const rect=this.getBoundingClientRect();
        const progress=reduced.matches||this.dataset.animate==='false'?0:Math.max(0,Math.min(1,(innerHeight*.85-rect.top-rect.height/2)/(innerHeight*.65)));
        const target=Math.max(10,Math.min(180,Number(this.dataset.targetAngle)||160));
        this.needle.setAttribute('transform',`rotate(${10+(target-10)*progress} 43 254)`);
      };
      this.schedule=()=>{if(!this.frame)this.frame=requestAnimationFrame(this.update);};
      addEventListener('scroll',this.schedule,{passive:true,signal});
      addEventListener('resize',this.schedule,{passive:true,signal});
      reduced.addEventListener('change',this.schedule,{signal});
      this.resize=new ResizeObserver(this.schedule);
      this.resize.observe(this);
      this.update();
    }
    disconnectedCallback(){this.controller?.abort();this.resize?.disconnect();cancelAnimationFrame(this.frame);}
  }
  if(!customElements.get('fte-protractor'))customElements.define('fte-protractor',ProtractorSweep);
  function updateStoryTilt(){
    document.querySelectorAll('.fte-story-panel--intro[data-story-tilt=true]').forEach(section => {
      const t=Math.max(0,Math.min(1,(innerHeight-section.getBoundingClientRect().top)/innerHeight));
      section.style.setProperty('--fte-story-angle',`${reduced.matches ? 0 : -4+8*t}deg`);
    });
  }
  addEventListener('scroll', updateStoryTilt,{passive:true});
  document.addEventListener('click',event=>{
    const card=event.target.closest('.fte-step');if(!card)return;
    const active=!card.classList.contains('is-active');
    card.parentElement.querySelectorAll('.fte-step').forEach(c=>{c.classList.remove('is-active');c.setAttribute('aria-pressed','false');});
    if(active){card.classList.add('is-active');card.setAttribute('aria-pressed','true');}
  });
  document.addEventListener('keydown',event=>{
    if(event.target.matches('.fte-step') && (event.key==='Enter'||event.key===' ')){event.preventDefault();event.target.click();}
    if(event.key==='Escape')document.querySelectorAll('.fte-step.is-active').forEach(c=>{c.classList.remove('is-active');c.setAttribute('aria-pressed','false');});
  });

  if (!customElements.get('fte-hero')) customElements.define('fte-hero', FlattenHero);
  if (!customElements.get('fte-slider')) customElements.define('fte-slider', DealSlider);
  if (!customElements.get('fte-gallery')) customElements.define('fte-gallery', class extends DealSlider {});
  if (!customElements.get('fte-video')) customElements.define('fte-video', CampaignVideo);
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (!dialog) return;
      const formUrl = opener.dataset.contestUrl;
      const isContest = Boolean(formUrl);
      const allowedForm = url => url.protocol === 'https:' && (
        url.hostname === 'gleam.io' || url.hostname.endsWith('.gleam.io') ||
        url.hostname === 'jotform.com' || url.hostname.endsWith('.jotform.com') ||
        url.hostname === 'jotformpro.com' || url.hostname.endsWith('.jotformpro.com'));
      if (isContest) {
        let url;
        try { url = new URL(formUrl, location.href); } catch { return; }
        if (!allowedForm(url)) return;
        // A rules click deep-links to the named question inside the same form.
        // Keep the ordinary entry button pointed to the form's beginning.
        const anchor = opener.dataset.contestAnchor?.replace(/^#/, '');
        if (anchor && /^[A-Za-z][A-Za-z0-9_-]*$/.test(anchor)) url.hash = anchor;
        const mode = matchMedia('(max-width: 749px)').matches ? opener.dataset.contestMobile : opener.dataset.contestDesktop;
        if (mode === 'tab') {
          window.open(url.href, '_blank', 'noopener,noreferrer');
          return;
        }
      }
      const template = dialog.querySelector('template');
      if (template) {
        const frame = template.content.querySelector('iframe');
        if (!frame) return;
        const url = new URL(frame.src, location.href);
        if (!allowedForm(url)) return;
        if (opener.dataset.contestAnchor) {
          const anchor = opener.dataset.contestAnchor.replace(/^#/, '');
          if (/^[A-Za-z][A-Za-z0-9_-]*$/.test(anchor)) url.hash = anchor;
        }
        frame.src = url.href;
        template.replaceWith(template.content.cloneNode(true));
      } else if (isContest) {
        // The iframe may already exist after an earlier entry-button click.
        // Reload only when changing between the entry top and the rules anchor.
        const frame = dialog.querySelector('iframe');
        if (frame) {
          const url = new URL(formUrl, location.href);
          const anchor = opener.dataset.contestAnchor?.replace(/^#/, '');
          if (anchor && /^[A-Za-z][A-Za-z0-9_-]*$/.test(anchor)) url.hash = anchor;
          if (allowedForm(url) && frame.src !== url.href) frame.src = url.href;
        }
      }
      if (typeof dialog.showModal !== 'function') return;
      dialog.showModal();
      document.body.classList.add('fte-dialog-open');
      dialog.addEventListener('close', () => {
        document.body.classList.remove('fte-dialog-open');
        opener.focus();
      }, { once: true });
    }
    event.target.closest('[data-close-dialog]')?.closest('dialog')?.close();
  });
  document.addEventListener('shopify:block:select', event => {
    const block = event.target;
    const track = block.closest('.fte-slider__track, .fte-gallery__track');
    if (track) track.scrollTo({ left: block.offsetLeft, behavior: 'instant' });
  });
})();



/* Fit Guide production sync verification 2026-11-01 */
